import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { selectToken } from 'src/app/store/selectors/auth.selectors';
import { environment } from 'src/environments/environment';

export interface AiReply {
  main?: string;
  clarification?: string;
  clarification_options?: string[];
  disclaimer?: string;
  follow_up_questions?: string[];
  sources?: AiSource[];
  contact_cse_recommended?: boolean;
  /** Free-text input must stay disabled until one of the options is clicked. */
  input_locked?: boolean;
}

export interface AiSource {
  doc_uuid?: string;
  title?: string;
  doc_title?: string;
  source_url?: string;
  viewer_url?: string;
  highlight_text?: string;
  article_ref?: string;
  page?: number;
  char_start?: number;
  char_end?: number;
  content?: string;
  excerpt?: string;
  snippet?: string;
  text?: string;
  similarity?: number;
}

export interface AiChatResponse {
  conversation_id?: number;
  message_id?: number;
  content?: string;
  reply?: AiReply;
  sources?: AiSource[];
}

/**
 * Monthly quota of the current user, as reported by GET /ai/chat/quota and by
 * the 429 body of the chat endpoints. `metered` is false for internal roles,
 * which have no cap. `exceeded` is true as soon as the next question would be
 * refused, whether by the personal or by the domain cap.
 */
export interface AiQuotaStatus {
  metered: boolean;
  limit: number | null;
  used: number;
  remaining: number | null;
  exceeded: boolean;
  /** Human-readable current period, e.g. "septembre 2026". */
  period_label: string;
  /** ISO date (YYYY-MM-DD) of the first day of the current period. */
  period_start?: string;
  /** ISO date (YYYY-MM-DD) on which the counters start again. */
  resets_at: string;
  domain?: {
    limit: number;
    used: number;
    remaining: number;
    exceeded: boolean;
  } | null;
}

export interface AiAskOptions {
  /**
   * The guided tour's sample question: the API answers it without charging
   * the quota, once per account.
   */
  onboarding?: boolean;
}

export type AiStreamEventName = 'meta' | 'text' | 'structured' | 'done' | 'error';

export interface AiStreamEvent {
  event: AiStreamEventName;
  data: {
    conversation_id?: number;
    text?: string;
    part?: 'main';
    reply?: AiReply;
    rag_sources?: AiSource[];
    status?: string;
    message?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AiService {
  private quotaSubject = new BehaviorSubject<AiQuotaStatus | undefined>(undefined);

  /**
   * Last known quota of the current user on the active domain; undefined until
   * the first successful load. Shared by the search bar (placeholder, counter,
   * keyword-search fallback) and the assistant overlay.
   */
  quota$ = this.quotaSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private store: Store,
  ) {}

  get quota(): AiQuotaStatus | undefined {
    return this.quotaSubject.value;
  }

  quotaStatus(domainUuid?: string): Observable<AiQuotaStatus> {
    const params: Record<string, string> = {};
    if (domainUuid) {
      params.domain_uuid = domainUuid;
    }

    return this.httpClient.get<AiQuotaStatus>(`${environment.apiUrl}/ai/chat/quota`, { params }).pipe(
      tap((status) => this.quotaSubject.next(status)),
    );
  }

  /**
   * Fire-and-forget refresh. A failure (assistant disabled for the domain,
   * network error) keeps the previous value: the chat endpoints stay the
   * source of truth and answer 429 themselves.
   */
  loadQuota(domainUuid?: string): void {
    this.quotaStatus(domainUuid).subscribe({
      next: () => {},
      error: () => {},
    });
  }

  /**
   * Flip the shared state to "exceeded" from a 429 body, so the search bar
   * falls back to the keyword search without waiting for another request.
   */
  markQuotaExceeded(payload?: Partial<AiQuotaStatus> | null): void {
    const current = this.quotaSubject.value;
    const limit = typeof payload?.limit === 'number' ? payload.limit : (current?.limit ?? null);

    this.quotaSubject.next({
      metered: true,
      limit,
      used: typeof payload?.used === 'number' ? payload.used : (limit ?? current?.used ?? 0),
      remaining: 0,
      exceeded: true,
      period_label: payload?.period_label || current?.period_label || '',
      period_start: payload?.period_start || current?.period_start,
      resets_at: payload?.resets_at || current?.resets_at || '',
      domain: current?.domain ?? null,
    });
  }

  clearQuota(): void {
    this.quotaSubject.next(undefined);
  }

  ask(question: string, domainUuid?: string, conversationId?: number, options: AiAskOptions = {}): Observable<AiChatResponse> {
    return this.httpClient.post<AiChatResponse>(`${environment.apiUrl}/ai/chat`, {
      question,
      domain_uuid: domainUuid || undefined,
      conversation_id: conversationId || undefined,
      onboarding: options.onboarding || undefined,
    });
  }

  stream(question: string, domainUuid?: string, conversationId?: number, options: AiAskOptions = {}): Observable<AiStreamEvent> {
    return new Observable<AiStreamEvent>((subscriber) => {
      const abortController = new AbortController();

      this.consumeStream(question, domainUuid, conversationId, options, abortController, (event) => {
        subscriber.next(event);
      }).then(() => {
        subscriber.complete();
      }).catch((error) => {
        if (!subscriber.closed && error?.name !== 'AbortError') {
          subscriber.error(error);
        }
      });

      return () => abortController.abort();
    });
  }

  /**
   * Records that the guided tour is over for this account, whatever the
   * device. Failures are the caller's business: the local flag is enough to
   * keep the tour closed on this device.
   */
  completeOnboarding(): Observable<{ ai_onboarding_completed_at?: string }> {
    return this.httpClient.post<{ ai_onboarding_completed_at?: string }>(`${environment.apiUrl}/me/ai-onboarding/complete`, {});
  }

  private async consumeStream(
    question: string,
    domainUuid: string | undefined,
    conversationId: number | undefined,
    options: AiAskOptions,
    abortController: AbortController,
    emit: (event: AiStreamEvent) => void,
  ): Promise<void> {
    const token = await this.store.select(selectToken).pipe(take(1)).toPromise();
    const storedDomain = await Preferences.get({ key: `${environment.application}-domain` });
    const response = await fetch(`${environment.apiUrl}/ai/chat/stream`, {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token || ''}`,
        Application: environment.application,
        Domain: storedDomain.value || domainUuid || '',
        IsMobile: 'true',
      },
      body: JSON.stringify({
        question,
        domain_uuid: domainUuid || undefined,
        conversation_id: conversationId || undefined,
        onboarding: options.onboarding || undefined,
      }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      let payload: any = {};
      try {
        payload = await response.json();
      } catch (_) {}
      const error: any = new Error(payload?.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.error = payload;
      throw error;
    }

    if (!response.body) {
      this.parseSseBuffer(await response.text(), emit, true);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      buffer = this.parseSseBuffer(buffer, emit, done);
      if (done) {
        break;
      }
    }
  }

  private parseSseBuffer(
    buffer: string,
    emit: (event: AiStreamEvent) => void,
    flush: boolean,
  ): string {
    const normalized = buffer.replace(/\r\n/g, '\n');
    const blocks = normalized.split('\n\n');
    const remainder = flush ? '' : (blocks.pop() || '');

    blocks.forEach((block) => this.emitSseBlock(block, emit));
    if (flush && blocks.length === 0 && normalized.trim()) {
      this.emitSseBlock(normalized, emit);
    }

    return remainder;
  }

  private emitSseBlock(block: string, emit: (event: AiStreamEvent) => void): void {
    if (!block.trim()) {
      return;
    }

    const lines = block.split('\n');
    const event = lines.find(line => line.startsWith('event:'))?.slice(6).trim() as AiStreamEventName;
    const data = lines
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).replace(/^\s+/, ''))
      .join('\n');

    if (!event || !data) {
      return;
    }

    try {
      emit({ event, data: JSON.parse(data) });
    } catch (_) {}
  }
}
